import React, { useState, useMemo, useEffect } from 'react';

const API_BASE = 'https://faredrift.com/api';

// DESTINATIONS DATA (fallback if API fails)
const DESTINATIONS = {
  europe: { name: 'Europe', emoji: '🏰', destinations: [
    { code: 'CDG', city: 'Paris', country: 'France', emoji: '🇫🇷', icon: '🎨', dream: 'sketching by the Seine at golden hour', color: '#E8B4BC' },
    { code: 'FCO', city: 'Rome', country: 'Italy', emoji: '🇮🇹', icon: '☕', dream: 'sipping espresso in a sun-drenched piazza', color: '#D4A574' },
    { code: 'BCN', city: 'Barcelona', country: 'Spain', emoji: '🇪🇸', icon: '🌊', dream: 'watching sunset from a rooftop tapas bar', color: '#F4A460' },
    { code: 'LHR', city: 'London', country: 'UK', emoji: '🇬🇧', icon: '🫖', dream: 'having afternoon tea while rain patters the windows', color: '#A0522D' },
    { code: 'AMS', city: 'Amsterdam', country: 'Netherlands', emoji: '🇳🇱', icon: '🚲', dream: 'cycling along canals lined with tulips', color: '#FF6B35' },
    { code: 'LIS', city: 'Lisbon', country: 'Portugal', emoji: '🇵🇹', icon: '🎸', dream: 'listening to fado in a candlelit tavern', color: '#E6B89C' },
    { code: 'ATH', city: 'Athens', country: 'Greece', emoji: '🇬🇷', icon: '🏛️', dream: 'watching sunrise paint the Acropolis gold', color: '#87CEEB' },
    { code: 'PRG', city: 'Prague', country: 'Czech Republic', emoji: '🇨🇿', icon: '🌉', dream: 'wandering cobblestone streets at twilight', color: '#DEB887' },
    { code: 'VIE', city: 'Vienna', country: 'Austria', emoji: '🇦🇹', icon: '🎻', dream: 'attending a concert in a gilded palace', color: '#FFD700' },
    { code: 'DUB', city: 'Dublin', country: 'Ireland', emoji: '🇮🇪', icon: '🍀', dream: 'sharing stories with locals in a cozy pub', color: '#228B22' },
    { code: 'KEF', city: 'Reykjavik', country: 'Iceland', emoji: '🇮🇸', icon: '🌌', dream: 'soaking in hot springs under Northern Lights', color: '#4B0082' },
    { code: 'VCE', city: 'Venice', country: 'Italy', emoji: '🇮🇹', icon: '🛶', dream: 'gliding through canals as church bells echo', color: '#40E0D0' },
  ]},
  asia: { name: 'Asia', emoji: '🏯', destinations: [
    { code: 'NRT', city: 'Tokyo', country: 'Japan', emoji: '🇯🇵', icon: '🌸', dream: 'watching cherry blossoms in a temple garden', color: '#FFB7C5' },
    { code: 'KIX', city: 'Osaka', country: 'Japan', emoji: '🇯🇵', icon: '🍜', dream: 'slurping ramen in a bustling food alley', color: '#FF6B6B' },
    { code: 'PEK', city: 'Beijing', country: 'China', emoji: '🇨🇳', icon: '🏯', dream: 'walking the Great Wall as mist rises', color: '#CD5C5C' },
    { code: 'PVG', city: 'Shanghai', country: 'China', emoji: '🇨🇳', icon: '🌃', dream: 'watching neon lights on the Bund at night', color: '#9370DB' },
    { code: 'TPE', city: 'Taipei', country: 'Taiwan', emoji: '🇹🇼', icon: '🏮', dream: 'exploring night markets full of flavors', color: '#FF4500' },
    { code: 'HKG', city: 'Hong Kong', country: 'Hong Kong', emoji: '🇭🇰', icon: '🌆', dream: 'riding Star Ferry as skyscrapers sparkle', color: '#4169E1' },
    { code: 'ICN', city: 'Seoul', country: 'South Korea', emoji: '🇰🇷', icon: '🎤', dream: 'discovering K-culture in Gangnam streets', color: '#FF69B4' },
    { code: 'BKK', city: 'Bangkok', country: 'Thailand', emoji: '🇹🇭', icon: '🛕', dream: 'finding peace in golden temples', color: '#FFD700' },
    { code: 'SIN', city: 'Singapore', country: 'Singapore', emoji: '🇸🇬', icon: '🌳', dream: 'wandering futuristic gardens at night', color: '#00CED1' },
    { code: 'HAN', city: 'Hanoi', country: 'Vietnam', emoji: '🇻🇳', icon: '🛵', dream: 'sipping Vietnamese coffee watching life flow', color: '#8B4513' },
  ]},
  pacific: { name: 'Pacific Islands', emoji: '🏝️', destinations: [
    { code: 'HNL', city: 'Honolulu', country: 'Hawaii', emoji: '🌺', icon: '🏄', dream: 'catching waves at sunrise over Diamond Head', color: '#FF6B6B' },
    { code: 'OGG', city: 'Maui', country: 'Hawaii', emoji: '🌺', icon: '🌅', dream: 'watching sunrise atop Haleakalā volcano', color: '#FF8C00' },
    { code: 'PPT', city: 'Tahiti', country: 'French Polynesia', emoji: '🇵🇫', icon: '🌊', dream: 'swimming in crystal lagoons', color: '#00CED1' },
    { code: 'NAN', city: 'Fiji', country: 'Fiji', emoji: '🇫🇯', icon: '🐚', dream: 'feeling white sand between your toes', color: '#20B2AA' },
    { code: 'RAR', city: 'Rarotonga', country: 'Cook Islands', emoji: '🇨🇰', icon: '🥥', dream: 'sipping coconut water in paradise', color: '#3CB371' },
    { code: 'GUM', city: 'Guam', country: 'Guam', emoji: '🇬🇺', icon: '🐠', dream: 'snorkeling through vibrant coral reefs', color: '#1E90FF' },
    { code: 'AKL', city: 'Auckland', country: 'New Zealand', emoji: '🇳🇿', icon: '⛵', dream: 'sailing the City of Sails at golden hour', color: '#4682B4' },
  ]},
  caribbean: { name: 'Caribbean', emoji: '🌴', destinations: [
    { code: 'SJU', city: 'San Juan', country: 'Puerto Rico', emoji: '🇵🇷', icon: '🏰', dream: 'wandering colorful Old San Juan streets', color: '#FF6347' },
    { code: 'CUN', city: 'Cancún', country: 'Mexico', emoji: '🇲🇽', icon: '🏖️', dream: 'lounging on white sand with turquoise waves', color: '#00CED1' },
    { code: 'MBJ', city: 'Montego Bay', country: 'Jamaica', emoji: '🇯🇲', icon: '🎶', dream: 'feeling reggae rhythms on a beach night', color: '#FFD700' },
    { code: 'PUJ', city: 'Punta Cana', country: 'Dominican Rep.', emoji: '🇩🇴', icon: '🌅', dream: 'waking to palm trees over paradise', color: '#FF8C00' },
    { code: 'NAS', city: 'Nassau', country: 'Bahamas', emoji: '🇧🇸', icon: '🐷', dream: 'swimming with pigs in crystal waters', color: '#FF69B4' },
    { code: 'AUA', city: 'Aruba', country: 'Aruba', emoji: '🇦🇼', icon: '🌵', dream: 'discovering hidden coves and divi-divi trees', color: '#F4A460' },
    { code: 'BGI', city: 'Barbados', country: 'Barbados', emoji: '🇧🇧', icon: '🍹', dream: 'sipping rum punch as the sun sets', color: '#DC143C' },
    { code: 'UVF', city: 'St. Lucia', country: 'St. Lucia', emoji: '🇱🇨', icon: '⛰️', dream: 'gazing at Pitons rising from the sea', color: '#228B22' },
  ]}
};

const ALL_DESTINATIONS = Object.values(DESTINATIONS).flatMap(r => r.destinations.map(d => ({ ...d, region: r.name })));

const generatePrices = (destinations) => {
  const today = new Date();
  return destinations.map((dest, idx) => {
    const regionBase = { 'Europe': 550, 'Asia': 850, 'Pacific Islands': 650, 'Caribbean': 400 };
    const base = (regionBase[dest.region] || 500) + (idx * 5);
    const weeks = 3 + (idx % 5) * 2;
    const dur = [7, 10, 14][idx % 3];
    const dep = new Date(today); dep.setDate(dep.getDate() + weeks * 7);
    const ret = new Date(dep); ret.setDate(ret.getDate() + dur);
    const price = Math.max(299, base + Math.floor(Math.random() * 200) - 50);
    const airlines = { 'Europe': ['United','Lufthansa','Delta'], 'Asia': ['ANA','JAL','United'], 'Pacific Islands': ['Hawaiian','United'], 'Caribbean': ['JetBlue','American','Delta'] };
    return { 
      id: `${dest.code}-0`, 
      destination: dest,
      code: dest.code,
      city: dest.city,
      country: dest.country,
      region: dest.region,
      emoji: dest.emoji,
      icon: dest.icon,
      dream: dest.dream,
      color: dest.color,
      departDate: dep, 
      returnDate: ret, 
      duration: dur, 
      price, 
      airline: airlines[dest.region]?.[idx % 3] || 'United', 
      stops: [0, 1, 2][Math.floor(Math.random() * 3)]
    };
  });
};

const Bird = ({ style, delay = 0, size = 1 }) => (
  <div style={{ position: 'absolute', animation: `fly ${20 + Math.random() * 10}s linear infinite`, animationDelay: `${delay}s`, opacity: 0.6, ...style }}>
    <svg width={30 * size} height={15 * size} viewBox="0 0 30 15"><path d="M0 8Q7 0 15 8Q23 0 30 8" stroke="#4A5568" strokeWidth="2.5" fill="none" strokeLinecap="round"/></svg>
  </div>
);

const Cloud = ({ top, delay = 0, size = 'md' }) => {
  const s = { sm: 80, md: 140, lg: 200 }[size];
  return (
    <div style={{ position: 'absolute', top, left: -250, width: s, height: s * 0.5, animation: `drift ${size === 'lg' ? 100 : size === 'md' ? 80 : 60}s linear infinite`, animationDelay: `${delay}s` }}>
      <div style={{ width: '100%', height: '100%', background: 'radial-gradient(ellipse, #fff 0%, #f0f4f8 100%)', borderRadius: 50, boxShadow: '0 8px 30px rgba(0,0,0,0.05)', position: 'relative' }}>
        <div style={{ position: 'absolute', width: '50%', height: '90%', background: 'radial-gradient(ellipse, #fff, #f7fafc)', borderRadius: '50%', top: '-40%', left: '10%' }} />
        <div style={{ position: 'absolute', width: '40%', height: '70%', background: 'radial-gradient(ellipse, #fff, #f7fafc)', borderRadius: '50%', top: '-25%', right: '15%' }} />
      </div>
    </div>
  );
};

export default function FlightsAndDreams() {
  const [isPremium, setIsPremium] = useState(false);
  const [userEmail, setUserEmail] = useState(localStorage.getItem('fd_email') || '');
  const [region, setRegion] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [showWishlist, setShowWishlist] = useState(false);
  const [adventure, setAdventure] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeEmail, setUpgradeEmail] = useState('');
  const [upgradePlan, setUpgradePlan] = useState('monthly');
  const [apiDeals, setApiDeals] = useState(null);
  const [dealOfDay, setDealOfDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailSignup, setEmailSignup] = useState('');
  const [emailSignupSuccess, setEmailSignupSuccess] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Fetch destinations from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch destinations with prices from API
        const destRes = await fetch(`${API_BASE}/destinations`);
        const destData = await destRes.json();
        if (destData.success && destData.data) {
          // Merge API price data with local emoji data
          const localDestMap = {};
          ALL_DESTINATIONS.forEach(d => {
            localDestMap[d.code] = d;
          });
          
          const transformed = destData.data.map(d => {
            const local = localDestMap[d.code] || {};
            return {
              id: `${d.code}-0`,
              destination: d,
              code: d.code,
              city: d.city,
              country: d.country,
              region: d.region,
              // Use LOCAL emoji/icon data (guaranteed to work)
              emoji: local.emoji || d.emoji,
              icon: local.icon || d.icon,
              dream: local.dream || d.dream,
              color: local.color || d.color,
              // Use API price data
              departDate: d.departDate ? new Date(d.departDate) : null,
              returnDate: d.returnDate ? new Date(d.returnDate) : null,
              duration: d.duration,
              price: d.price,
              airline: d.airline,
              stops: d.stops
            };
          });
          setApiDeals(transformed);
        }

        // Fetch deal of the day
        const dodRes = await fetch(`${API_BASE}/deal-of-day`);
        const dodData = await dodRes.json();
        if (dodData.success && dodData.data) {
          const d = dodData.data;
          const localDestMap = {};
          ALL_DESTINATIONS.forEach(dest => {
            localDestMap[dest.code] = dest;
          });
          const local = localDestMap[d.code] || {};
          
          setDealOfDay({
            id: `${d.code}-dod`,
            destination: d,
            code: d.code,
            city: d.city,
            country: d.country,
            region: d.region,
            // Use LOCAL emoji/icon data
            emoji: local.emoji || d.emoji,
            icon: local.icon || d.icon,
            dream: local.dream || d.dream,
            color: local.color || d.color,
            // Use API price data
            departDate: d.departDate ? new Date(d.departDate) : null,
            returnDate: d.returnDate ? new Date(d.returnDate) : null,
            duration: d.duration,
            price: d.price,
            airline: d.airline,
            stops: d.stops
          });
        }
      } catch (err) {
        console.log('API fetch failed, using fallback data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Check premium status
  useEffect(() => {
    const checkPremium = async () => {
      const email = localStorage.getItem('fd_email');
      if (email) {
        try {
          const res = await fetch(`${API_BASE}/payments/status?email=${encodeURIComponent(email)}`);
          const data = await res.json();
          if (data.success && data.isPremium) {
            setIsPremium(true);
          }
        } catch (err) {
          console.log('Could not check premium status');
        }
      }
    };
    checkPremium();
  }, []);

  // Use API data or fallback - only show destinations with prices
  const deals = useMemo(() => {
    let data = apiDeals || generatePrices(ALL_DESTINATIONS);
    // Filter out destinations without prices
    data = data.filter(d => d.price && d.price > 0);
    if (region !== 'all') {
      data = data.filter(d => (d.region || d.destination?.region) === region);
    }
    return data.sort((a, b) => (a.price || 999) - (b.price || 999));
  }, [apiDeals, region]);

  const currentDealOfDay = dealOfDay || (deals.length > 0 ? deals[0] : null);
  
  const toggleWish = (deal) => setWishlist(prev => prev.some(d => d.id === deal.id) ? prev.filter(d => d.id !== deal.id) : [...prev, deal]);
  const inWish = (deal) => wishlist.some(d => d.id === deal.id);
  const spin = () => { setSpinning(true); let i = 0; const int = setInterval(() => { setAdventure(deals[Math.floor(Math.random() * deals.length)]); if (++i >= 20) { clearInterval(int); setSpinning(false); } }, 100); };
  
  const fmt = (d) => {
    if (!d) return '---';
    if (typeof d === 'string') d = new Date(d);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const fmtFull = (d) => {
    if (!d) return '---';
    if (typeof d === 'string') d = new Date(d);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const fmtMonth = (d) => {
    if (!d) return '---';
    if (typeof d === 'string') d = new Date(d);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const stopsText = (stops) => {
    if (stops === null || stops === undefined) return '---';
    if (stops === 0) return 'Nonstop ✨';
    if (stops === 1) return '1 stop';
    return `${stops} stops`;
  };

  // Handle Stripe checkout
  const handleUpgrade = async () => {
    if (!upgradeEmail || !upgradeEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setUpgradeLoading(true);

    try {
      const res = await fetch(`${API_BASE}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: upgradeEmail,
          plan: upgradePlan
        })
      });

      const data = await res.json();

      if (data.success && data.checkoutUrl) {
        // Save email for later
        localStorage.setItem('fd_email', upgradeEmail);
        // Redirect to Stripe
        window.location.href = data.checkoutUrl;
      } else {
        alert(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      alert('Could not connect to payment system. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Handle email signup for deal alerts
  const handleEmailSignup = async () => {
    if (!emailSignup || !emailSignup.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailSignup,
          source: 'deal_alerts_free'
        })
      });

      const data = await res.json();
      if (data.success) {
        setEmailSignupSuccess(true);
        localStorage.setItem('fd_email', emailSignup);
      }
    } catch (err) {
      alert('Could not subscribe. Please try again.');
    }
  };

  // Check for successful payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_id')) {
      setIsPremium(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      alert('🎉 Welcome to Premium! You now have full access to all flight details and daily deal emails.');
    }
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #7EC8E3 0%, #A7D8DE 25%, #F5E6A3 65%, #E8C98B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Nunito', sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>✈️</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#3D2914' }}>Loading dreams...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #7EC8E3 0%, #A7D8DE 25%, #F5E6A3 65%, #E8C98B 100%)', fontFamily: "'Nunito', sans-serif", color: '#3D2914', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; }
        @keyframes fly { 0% { transform: translateX(0) translateY(0); } 25% { transform: translateX(25vw) translateY(-8px); } 50% { transform: translateX(50vw) translateY(5px); } 75% { transform: translateX(75vw) translateY(-5px); } 100% { transform: translateX(105vw) translateY(0); } }
        @keyframes drift { 0% { transform: translateX(0); } 100% { transform: translateX(calc(100vw + 300px)); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .card { background: rgba(255,255,255,0.95); border-radius: 20px; padding: 20px; box-shadow: 0 8px 32px rgba(139,69,19,0.1); transition: all 0.3s ease; cursor: pointer; position: relative; overflow: hidden; }
        .card:hover { transform: translateY(-6px); box-shadow: 0 16px 48px rgba(139,69,19,0.15); }
        .card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: var(--accent); }
        .btn { background: linear-gradient(135deg, #FF6B6B, #FF8E53); border: none; border-radius: 50px; padding: 14px 28px; color: white; font-size: 16px; font-weight: 700; cursor: pointer; font-family: 'Nunito'; box-shadow: 0 6px 20px rgba(255,107,107,0.3); transition: all 0.2s; }
        .btn:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(255,107,107,0.4); }
        .btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
        .tab { padding: 10px 18px; border-radius: 50px; border: none; font-family: 'Nunito'; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
        .tab.on { background: linear-gradient(135deg, #FF6B6B, #FF8E53); color: white; box-shadow: 0 4px 12px rgba(255,107,107,0.3); }
        .tab:not(.on) { background: rgba(255,255,255,0.85); color: #5D4427; }
        .tab:not(.on):hover { background: white; }
        .price { font-size: 32px; font-weight: 900; color: #2E7D32; }
        .modal { position: fixed; inset: 0; background: rgba(61,41,20,0.6); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
        .modal-box { background: linear-gradient(180deg, #FFF8E7, #FFF); border-radius: 28px; max-width: 480px; width: 100%; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.25); animation: slideUp 0.3s ease; }
        .fab { position: fixed; bottom: 24px; right: 24px; width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #FF6B6B, #FF8E53); border: none; color: white; font-size: 26px; cursor: pointer; box-shadow: 0 6px 24px rgba(255,107,107,0.4); z-index: 100; display: flex; align-items: center; justify-content: center; transition: transform 0.2s; }
        .fab:hover { transform: scale(1.1); }
        .plan-option { border: 2px solid #E0E0E0; border-radius: 16px; padding: 16px; cursor: pointer; transition: all 0.2s; }
        .plan-option.selected { border-color: #667eea; background: rgba(102,126,234,0.05); }
        .plan-option:hover { border-color: #667eea; }
      `}</style>

      {/* Clouds */}
      <Cloud top="5%" size="lg" delay={0} />
      <Cloud top="12%" size="md" delay={15} />
      <Cloud top="8%" size="sm" delay={30} />
      <Cloud top="18%" size="lg" delay={45} />
      <Cloud top="22%" size="md" delay={60} />
      <Cloud top="15%" size="sm" delay={75} />

      {/* Birds */}
      <Bird style={{ top: '6%' }} delay={0} size={0.9} />
      <Bird style={{ top: '8%' }} delay={0.4} size={0.7} />
      <Bird style={{ top: '5%' }} delay={0.8} size={0.8} />
      <Bird style={{ top: '14%' }} delay={5} size={1} />
      <Bird style={{ top: '16%' }} delay={5.5} size={0.75} />
      <Bird style={{ top: '24%' }} delay={10} size={0.85} />
      <Bird style={{ top: '26%' }} delay={10.6} size={0.7} />
      <Bird style={{ top: '11%' }} delay={16} size={1.1} />
      <Bird style={{ top: '20%' }} delay={22} size={0.8} />

      <div style={{ position: 'relative', zIndex: 1, padding: '40px 20px', maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <h1 style={{ fontSize: 'clamp(32px, 7vw, 56px)', fontWeight: 900, margin: 0, color: '#3D2914', textShadow: '2px 2px 0 #fff' }}>✨ Flights & Dreams</h1>
          <p style={{ fontSize: 18, color: '#5D4427', margin: '10px 0 0', fontWeight: 600 }}>Cheap flight deals from Denver (DEN)</p>
          <p style={{ fontSize: 14, color: '#8B7355', margin: '6px 0 0' }}>Updated daily • Round-trip prices</p>
          
          <div style={{ marginTop: 14, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            {isPremium ? (
              <div style={{ padding: '8px 20px', borderRadius: 50, background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', fontWeight: 700, fontSize: 13, display: 'inline-block' }}>
                👑 Premium Member
              </div>
            ) : (
              <>
                <button onClick={() => setShowUpgrade(true)} style={{ padding: '8px 20px', borderRadius: 50, border: 'none', background: 'rgba(255,255,255,0.85)', color: '#5D4427', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Nunito' }}>
                  🔓 Upgrade to Premium
                </button>
                <button onClick={() => setShowLogin(true)} style={{ padding: '8px 20px', borderRadius: 50, border: '2px solid rgba(93,68,39,0.2)', background: 'transparent', color: '#5D4427', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'Nunito' }}>
                  Already Premium? Log in
                </button>
              </>
            )}
          </div>
        </div>

        {/* Region Tabs */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 28 }}>
          {[{ id: 'all', name: 'All', emoji: '🌍' }, { id: 'Europe', name: 'Europe', emoji: '🏰' }, { id: 'Asia', name: 'Asia', emoji: '🏯' }, { id: 'Pacific Islands', name: 'Pacific', emoji: '🏝️' }, { id: 'Caribbean', name: 'Caribbean', emoji: '🌴' }].map(r => (
            <button key={r.id} className={`tab ${region === r.id ? 'on' : ''}`} onClick={() => setRegion(r.id)}>{r.emoji} {r.name}</button>
          ))}
        </div>

        {/* Deal of the Day */}
        {currentDealOfDay && (
          <div style={{ background: 'linear-gradient(135deg, #FFE4B5, #FFDAB9)', borderRadius: 28, padding: 28, marginBottom: 36, boxShadow: '0 12px 40px rgba(139,69,19,0.12)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -20, right: -20, fontSize: 100, opacity: 0.12 }}>{currentDealOfDay.icon || currentDealOfDay.destination?.icon}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ background: 'linear-gradient(135deg, #FF6B6B, #FF8E53)', color: 'white', padding: '7px 14px', borderRadius: 50, fontSize: 13, fontWeight: 800, animation: 'pulse 2s infinite' }}>⭐ Deal of the Day</span>
              <span style={{ background: 'rgba(93,68,39,0.1)', padding: '5px 10px', borderRadius: 16, fontSize: 11, fontWeight: 600, color: '#5D4427' }}>{currentDealOfDay.region || currentDealOfDay.destination?.region}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 44 }}>{currentDealOfDay.emoji || currentDealOfDay.destination?.emoji}</span>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 28, fontWeight: 800 }}>{currentDealOfDay.city || currentDealOfDay.destination?.city}</h2>
                    <p style={{ margin: 0, color: '#8B7355', fontWeight: 600, fontSize: 14 }}>{currentDealOfDay.country || currentDealOfDay.destination?.country}</p>
                  </div>
                </div>
                <p style={{ fontSize: 16, color: '#5D4427', margin: '14px 0', fontStyle: 'italic' }}><span style={{ fontSize: 22 }}>{currentDealOfDay.icon || currentDealOfDay.destination?.icon}</span> Imagine yourself... {currentDealOfDay.dream || currentDealOfDay.destination?.dream}</p>
                {/* PREMIUM: Show full details */}
                {isPremium ? (
                  <>
                    <p style={{ margin: 0, color: '#8B7355', fontSize: 13, fontWeight: 600 }}>
                      {currentDealOfDay.departDate ? `${fmtFull(currentDealOfDay.departDate)} - ${fmtFull(currentDealOfDay.returnDate)}` : 'Dates coming soon'} 
                      {currentDealOfDay.duration ? ` • ${currentDealOfDay.duration} days` : ''}
                    </p>
                    <p style={{ margin: '4px 0 0', color: '#A99275', fontSize: 12 }}>
                      {stopsText(currentDealOfDay.stops)} {currentDealOfDay.airline ? `• ${currentDealOfDay.airline}` : ''}
                    </p>
                  </>
                ) : (
                  <>
                    <p style={{ margin: 0, color: '#8B7355', fontSize: 13, fontWeight: 600 }}>
                      {currentDealOfDay.departDate ? fmtMonth(currentDealOfDay.departDate) : 'Coming soon'}
                    </p>
                    <div onClick={() => setShowUpgrade(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '6px 12px', background: 'rgba(102,126,234,0.1)', borderRadius: 10, cursor: 'pointer', border: '1px dashed rgba(102,126,234,0.3)' }}>
                      <span style={{ fontSize: 11, color: '#667eea', fontWeight: 600 }}>🔒 Dates & details with Premium</span>
                    </div>
                  </>
                )}
              </div>
              <div style={{ textAlign: 'center' }}>
                <div className="price">${currentDealOfDay.price || '---'}</div>
                <p style={{ margin: '4px 0 14px', color: '#8B7355', fontSize: 13 }}>round trip</p>
                <button className="btn" onClick={() => { setSelected(currentDealOfDay); setShowModal(true); }}>See Details ✨</button>
              </div>
            </div>
          </div>
        )}

        {/* Adventure Spinner */}
        <div style={{ background: 'rgba(255,255,255,0.92)', borderRadius: 22, padding: 28, marginBottom: 36, textAlign: 'center', boxShadow: '0 8px 32px rgba(139,69,19,0.08)' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>🎰 Adventure Awaits</h3>
          <p style={{ margin: '0 0 18px', color: '#8B7355', fontWeight: 500 }}>Can't decide? Let fate choose!</p>
          {adventure && !spinning && (
            <div style={{ background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)', borderRadius: 14, padding: 18, marginBottom: 18, animation: 'slideUp 0.4s' }}>
              <span style={{ fontSize: 44 }}>{adventure.emoji || adventure.destination?.emoji}</span>
              <h4 style={{ margin: '6px 0 2px', fontSize: 22, fontWeight: 800 }}>{adventure.city || adventure.destination?.city}!</h4>
              <p style={{ margin: 0, color: '#2E7D32', fontWeight: 700, fontSize: 18 }}>${adventure.price || '---'}</p>
              <button onClick={() => { setSelected(adventure); setShowModal(true); }} style={{ marginTop: 10, background: '#2E7D32', color: 'white', border: 'none', borderRadius: 50, padding: '8px 20px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito' }}>Explore →</button>
            </div>
          )}
          <button className="btn" onClick={spin} disabled={spinning} style={{ opacity: spinning ? 0.7 : 1 }}>{spinning ? '🎲 Spinning...' : '🎲 Spin for Adventure!'}</button>
        </div>

        {/* Email Signup for Free Users */}
        {!isPremium && (
          <div style={{ background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)', borderRadius: 22, padding: 28, marginBottom: 36, textAlign: 'center', boxShadow: '0 8px 32px rgba(139,69,19,0.08)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 800, color: '#2E7D32' }}>📧 Get Weekly Deal Alerts</h3>
            <p style={{ margin: '0 0 18px', color: '#5D4427', fontWeight: 500 }}>Free weekly email with the best flight deals!</p>
            {emailSignupSuccess ? (
              <div style={{ color: '#2E7D32', fontWeight: 700 }}>✅ You're signed up! Check your inbox.</div>
            ) : (
              <div style={{ display: 'flex', gap: 10, maxWidth: 400, margin: '0 auto', flexWrap: 'wrap', justifyContent: 'center' }}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={emailSignup}
                  onChange={(e) => setEmailSignup(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: 200,
                    padding: '12px 18px',
                    borderRadius: 50,
                    border: '2px solid #C8E6C9',
                    fontSize: 14,
                    fontFamily: 'Nunito',
                    outline: 'none'
                  }}
                />
                <button onClick={handleEmailSignup} style={{ background: '#2E7D32', color: 'white', border: 'none', borderRadius: 50, padding: '12px 24px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito' }}>Subscribe Free</button>
              </div>
            )}
            <p style={{ margin: '14px 0 0', color: '#5D4427', fontSize: 12 }}>Want daily emails + airline info? <span onClick={() => setShowUpgrade(true)} style={{ color: '#667eea', cursor: 'pointer', fontWeight: 700 }}>Upgrade to Premium</span></p>
          </div>
        )}

        {/* Results */}
        <h3 style={{ margin: '0 0 22px', fontSize: 26, fontWeight: 800, textAlign: 'center' }}>🌍 {deals.length} Dream Destinations</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {deals.map(deal => (
            <div key={deal.id} className="card" style={{ '--accent': deal.color || deal.destination?.color }} onClick={() => { setSelected(deal); setShowModal(true); }}>
              <span style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(93,68,39,0.08)', padding: '3px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, color: '#8B7355' }}>{deal.region || deal.destination?.region}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 36 }}>{deal.emoji || deal.destination?.emoji}</span>
                <div>
                  <h4 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{deal.city || deal.destination?.city}</h4>
                  <p style={{ margin: 0, color: '#8B7355', fontSize: 13, fontWeight: 600 }}>{deal.country || deal.destination?.country}</p>
                </div>
              </div>
              <p style={{ margin: '0 0 14px', color: '#5D4427', fontSize: 14, fontStyle: 'italic' }}><span style={{ fontSize: 18, marginRight: 4 }}>{deal.icon || deal.destination?.icon}</span>Imagine yourself... {deal.dream || deal.destination?.dream}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                  {isPremium ? (
                    <>
                      <p style={{ margin: 0, color: '#8B7355', fontSize: 12, fontWeight: 600 }}>{deal.departDate ? `${fmt(deal.departDate)} - ${fmt(deal.returnDate)}` : 'Dates coming soon'}</p>
                      <p style={{ margin: '2px 0 0', color: '#A99275', fontSize: 11 }}>{deal.duration ? `${deal.duration}d` : ''} • {stopsText(deal.stops)} {deal.airline ? `• ${deal.airline}` : ''}</p>
                    </>
                  ) : (
                    <>
                      <p style={{ margin: 0, color: '#8B7355', fontSize: 12, fontWeight: 600 }}>{deal.departDate ? fmtMonth(deal.departDate) : 'Coming soon'}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#667eea', fontSize: 10, fontWeight: 600, marginTop: 2 }}><span>🔒</span>Dates & details with Premium</div>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="price" style={{ fontSize: 26 }}>${deal.price || '---'}</div>
                  <button onClick={(e) => { e.stopPropagation(); toggleWish(deal); }} style={{ background: inWish(deal) ? '#FFEBEE' : 'rgba(255,255,255,0.9)', border: 'none', fontSize: 18, cursor: 'pointer', padding: 8, borderRadius: 50, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'transform 0.2s' }}>{inWish(deal) ? '❤️' : '🤍'}</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Wishlist Summary */}
        {wishlist.length > 0 && (
          <div style={{ marginTop: 36, background: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)', borderRadius: 22, padding: 28, textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 800, color: '#1565C0' }}>✨ Your Dream List</h3>
            <p style={{ margin: '0 0 14px', color: '#5D4427' }}>You have {wishlist.length} adventure{wishlist.length > 1 ? 's' : ''} waiting!</p>
            <button onClick={() => setShowWishlist(true)} style={{ background: '#1565C0', color: 'white', border: 'none', borderRadius: 50, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito' }}>View My Dreams →</button>
          </div>
        )}
      </div>

      {/* FAB */}
      {wishlist.length > 0 && <button className="fab" onClick={() => setShowWishlist(true)}>❤️<span style={{ position: 'absolute', top: -4, right: -4, background: '#3D2914', color: 'white', width: 22, height: 22, borderRadius: '50%', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{wishlist.length}</span></button>}

      {/* Detail Modal */}
      {showModal && selected && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ background: `linear-gradient(135deg, ${(selected.color || selected.destination?.color)}50, ${(selected.color || selected.destination?.color)}25)`, padding: 28, textAlign: 'center', position: 'relative' }}>
              <button onClick={() => setShowModal(false)} style={{ position: 'absolute', top: 14, right: 14, background: 'white', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>×</button>
              <span style={{ background: 'rgba(93,68,39,0.1)', padding: '5px 12px', borderRadius: 16, fontSize: 11, fontWeight: 600, color: '#5D4427' }}>{selected.region || selected.destination?.region}</span>
              <span style={{ fontSize: 56, display: 'block', margin: '14px 0' }}>{selected.emoji || selected.destination?.emoji}</span>
              <h2 style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 900 }}>{selected.city || selected.destination?.city}</h2>
              <p style={{ margin: 0, color: '#5D4427', fontWeight: 600 }}>{selected.country || selected.destination?.country}</p>
              <div className="price" style={{ marginTop: 14 }}>${selected.price || '---'}</div>
              <p style={{ margin: '4px 0 0', color: '#8B7355', fontSize: 13 }}>round trip from Denver</p>
            </div>
            <div style={{ padding: '20px 28px', background: '#FFF8E7' }}>
              <p style={{ margin: 0, fontSize: 16, fontStyle: 'italic', color: '#5D4427', textAlign: 'center' }}><span style={{ fontSize: 28, display: 'block', marginBottom: 6 }}>{selected.icon || selected.destination?.icon}</span>Imagine yourself... {selected.dream || selected.destination?.dream}</p>
            </div>
            <div style={{ padding: '20px 28px' }}>
              <h4 style={{ margin: '0 0 14px', fontWeight: 800 }}>✈️ Trip Details</h4>
              <div style={{ background: '#F5F5F5', borderRadius: 14, padding: 18, marginBottom: 18 }}>
                {isPremium ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}><span style={{ color: '#8B7355', fontWeight: 600 }}>Depart</span><span style={{ fontWeight: 700 }}>{selected.departDate ? fmtFull(selected.departDate) : 'Coming soon'}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}><span style={{ color: '#8B7355', fontWeight: 600 }}>Return</span><span style={{ fontWeight: 700 }}>{selected.returnDate ? fmtFull(selected.returnDate) : 'Coming soon'}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}><span style={{ color: '#8B7355', fontWeight: 600 }}>Duration</span><span style={{ fontWeight: 700 }}>{selected.duration ? `${selected.duration} days` : '---'}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}><span style={{ color: '#8B7355', fontWeight: 600 }}>Airline</span><span style={{ fontWeight: 700 }}>{selected.airline || '---'}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#8B7355', fontWeight: 600 }}>Route</span><span style={{ fontWeight: 700 }}>{stopsText(selected.stops)}</span></div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}><span style={{ color: '#8B7355', fontWeight: 600 }}>Travel Month</span><span style={{ fontWeight: 700 }}>{selected.departDate ? fmtMonth(selected.departDate) : 'Coming soon'}</span></div>
                    <div style={{ borderTop: '1px dashed #E0E0E0', marginTop: 10, paddingTop: 14 }}>
                      <div onClick={() => { setShowModal(false); setShowUpgrade(true); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                        <div>
                          <span style={{ color: '#667eea', fontWeight: 600, fontSize: 13 }}>🔒 Unlock full details</span>
                          <p style={{ margin: '4px 0 0', color: '#8B7355', fontSize: 11 }}>Exact dates, duration, airline & stops</p>
                        </div>
                        <span style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700 }}>Upgrade</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Booking links - Premium only */}
              <h4 style={{ margin: '0 0 10px', fontWeight: 800 }}>🔍 Search This Flight</h4>
              {isPremium ? (
                ['Google Flights', 'Skyscanner', 'Kayak'].map(s => {
                  const searchUrl = s === 'Google Flights' 
                    ? `https://www.google.com/travel/flights?q=DEN+to+${selected.code || selected.destination?.code}+${selected.departDate ? new Date(selected.departDate).toISOString().split('T')[0] : ''}`
                    : s === 'Skyscanner'
                    ? `https://www.skyscanner.com/transport/flights/den/${(selected.code || selected.destination?.code).toLowerCase()}/`
                    : `https://www.kayak.com/flights/DEN-${selected.code || selected.destination?.code}/`;
                  return (
                    <a key={s} href={searchUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: '#F5F5F5', borderRadius: 10, textDecoration: 'none', color: '#3D2914', fontWeight: 600, marginBottom: 8 }}><span>{s}</span><span style={{ color: '#FF6B6B' }}>Search →</span></a>
                  );
                })
              ) : (
                <div style={{ position: 'relative' }}>
                  {['Google Flights', 'Skyscanner', 'Kayak'].map(s => (
                    <div key={s} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: '#F5F5F5', borderRadius: 10, color: '#ccc', fontWeight: 600, marginBottom: 8, filter: 'blur(2px)', userSelect: 'none' }}><span>{s}</span><span>Search →</span></div>
                  ))}
                  <div onClick={() => { setShowModal(false); setShowUpgrade(true); }} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', borderRadius: 10, cursor: 'pointer' }}>
                    <div style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', padding: '10px 20px', borderRadius: 50, fontWeight: 700, fontSize: 14 }}>🔒 Unlock Booking Links</div>
                  </div>
                </div>
              )}

              <button onClick={() => toggleWish(selected)} style={{ width: '100%', marginTop: 18, padding: '14px', background: inWish(selected) ? '#FFEBEE' : 'linear-gradient(135deg, #FF6B6B, #FF8E53)', border: inWish(selected) ? '2px solid #FF6B6B' : 'none', borderRadius: 14, color: inWish(selected) ? '#FF6B6B' : 'white', fontSize: 16, fontWeight: 800, cursor: 'pointer', fontFamily: 'Nunito' }}>{inWish(selected) ? '❤️ In My Dreams' : '🤍 Add to My Dreams'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Wishlist Modal */}
      {showWishlist && (
        <div className="modal" onClick={() => setShowWishlist(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ padding: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>❤️ My Dreams</h2>
                <button onClick={() => setShowWishlist(false)} style={{ background: '#F5F5F5', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 18, cursor: 'pointer' }}>×</button>
              </div>
              {wishlist.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 36, color: '#8B7355' }}><span style={{ fontSize: 44 }}>💭</span><p>Your dream list is empty.</p></div>
              ) : (
                wishlist.map(d => (
                  <div key={d.id} style={{ background: '#F9F9F9', borderRadius: 14, padding: 14, display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                    <span style={{ fontSize: 32 }}>{d.emoji || d.destination?.emoji}</span>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, fontWeight: 800 }}>{d.city || d.destination?.city}</h4>
                      <p style={{ margin: '2px 0 0', color: '#8B7355', fontSize: 11 }}>{d.region || d.destination?.region}</p>
                      {d.departDate && <p style={{ margin: '3px 0 0', color: '#A99275', fontSize: 11 }}>{fmt(d.departDate)} - {fmt(d.returnDate)}</p>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 900, color: '#2E7D32', fontSize: 18 }}>${d.price || '---'}</div>
                      <button onClick={() => toggleWish(d)} style={{ background: 'none', border: 'none', color: '#FF6B6B', fontSize: 11, cursor: 'pointer', fontWeight: 600, padding: 0 }}>Remove</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgrade && (
        <div className="modal" onClick={() => setShowUpgrade(false)}>
          <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 36, textAlign: 'center' }}>
              <span style={{ fontSize: 56 }}>👑</span>
              <h2 style={{ margin: '12px 0 6px', fontSize: 26, fontWeight: 900 }}>Unlock Premium</h2>
              <p style={{ margin: '0 0 22px', color: '#5D4427' }}>Get the full experience</p>
              
              <div style={{ background: '#F5F5F5', borderRadius: 18, padding: 20, marginBottom: 20, textAlign: 'left' }}>
                {[
                  { feature: 'Exact travel dates', desc: 'Know the departure & return dates' },
                  { feature: 'Trip duration', desc: 'See how many days each trip is' },
                  { feature: 'Airline information', desc: 'Know who you\'re flying with' },
                  { feature: 'Number of stops', desc: 'Nonstop, 1 stop, or 2 stops' },
                  { feature: 'Daily deal emails', desc: 'Best prices delivered to your inbox' },
                ].map(f => (
                  <div key={f.feature} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                    <span style={{ color: '#2E7D32', fontSize: 16, marginTop: 2 }}>✓</span>
                    <div>
                      <span style={{ fontWeight: 700 }}>{f.feature}</span>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8B7355' }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Plan Selection */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <div 
                  className={`plan-option ${upgradePlan === 'monthly' ? 'selected' : ''}`}
                  onClick={() => setUpgradePlan('monthly')}
                  style={{ flex: 1, textAlign: 'center' }}
                >
                  <div style={{ fontWeight: 800, fontSize: 18 }}>$4.99</div>
                  <div style={{ fontSize: 12, color: '#8B7355' }}>per month</div>
                </div>
                <div 
                  className={`plan-option ${upgradePlan === 'yearly' ? 'selected' : ''}`}
                  onClick={() => setUpgradePlan('yearly')}
                  style={{ flex: 1, textAlign: 'center', position: 'relative' }}
                >
                  <div style={{ position: 'absolute', top: -8, right: -8, background: '#2E7D32', color: 'white', fontSize: 9, padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>SAVE 18%</div>
                  <div style={{ fontWeight: 800, fontSize: 18 }}>$49</div>
                  <div style={{ fontSize: 12, color: '#8B7355' }}>per year</div>
                </div>
              </div>

              {/* Email Input */}
              <input
                type="email"
                placeholder="Enter your email"
                value={upgradeEmail}
                onChange={(e) => setUpgradeEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  borderRadius: 12,
                  border: '2px solid #E0E0E0',
                  fontSize: 16,
                  fontFamily: 'Nunito',
                  marginBottom: 16,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button 
                onClick={handleUpgrade}
                disabled={upgradeLoading}
                style={{ 
                  width: '100%', 
                  background: 'linear-gradient(135deg, #667eea, #764ba2)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: 50, 
                  padding: '14px', 
                  fontWeight: 800, 
                  fontSize: 16, 
                  cursor: upgradeLoading ? 'not-allowed' : 'pointer', 
                  fontFamily: 'Nunito', 
                  boxShadow: '0 6px 20px rgba(102,126,234,0.3)',
                  opacity: upgradeLoading ? 0.7 : 1
                }}
              >
                {upgradeLoading ? 'Loading...' : `Start Premium - ${upgradePlan === 'yearly' ? '$49/year' : '$4.99/month'}`}
              </button>
              
              <button onClick={() => setShowUpgrade(false)} style={{ marginTop: 10, background: 'none', border: 'none', color: '#8B7355', cursor: 'pointer', fontFamily: 'Nunito' }}>Maybe later</button>
              
              <p style={{ margin: '16px 0 0', fontSize: 11, color: '#A99275' }}>Secure payment powered by Stripe</p>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLogin && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(4px)' }} onClick={() => setShowLogin(false)}>
          <div style={{ background: 'white', borderRadius: 28, padding: 32, maxWidth: 400, width: '100%', boxShadow: '0 25px 80px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', textAlign: 'center', fontSize: 24 }}>👋 Welcome Back</h2>
            <p style={{ textAlign: 'center', color: '#8B7355', marginBottom: 24 }}>Enter your email to access premium features</p>
            
            {loginError && (
              <div style={{ background: '#FFEBEE', color: '#C62828', padding: 12, borderRadius: 10, marginBottom: 16, textAlign: 'center', fontSize: 14 }}>
                {loginError}
              </div>
            )}
            
            <input
              type="email"
              placeholder="Enter your email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 18px',
                borderRadius: 12,
                border: '2px solid #E0E0E0',
                fontSize: 16,
                fontFamily: 'Nunito',
                marginBottom: 16,
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            
            <button 
              onClick={async () => {
                if (!loginEmail) {
                  setLoginError('Please enter your email');
                  return;
                }
                setLoginLoading(true);
                setLoginError('');
                try {
                  const res = await fetch(`${API_BASE}/payments/status?email=${encodeURIComponent(loginEmail)}`);
                  const data = await res.json();
                  if (data.success && data.isPremium) {
                    localStorage.setItem('fd_email', loginEmail);
                    localStorage.setItem('userEmail', loginEmail);
                    localStorage.setItem('isPremium', 'true');
                    setIsPremium(true);
                    setUserEmail(loginEmail);
                    setShowLogin(false);
                  } else {
                    setLoginError('No premium subscription found for this email. Would you like to upgrade?');
                  }
                } catch (err) {
                  setLoginError('Could not verify subscription. Please try again.');
                }
                setLoginLoading(false);
              }}
              disabled={loginLoading}
              style={{ 
                width: '100%', 
                background: 'linear-gradient(135deg, #667eea, #764ba2)', 
                color: 'white', 
                border: 'none', 
                borderRadius: 50, 
                padding: '14px', 
                fontWeight: 800, 
                fontSize: 16, 
                cursor: loginLoading ? 'not-allowed' : 'pointer', 
                fontFamily: 'Nunito', 
                boxShadow: '0 6px 20px rgba(102,126,234,0.3)',
                opacity: loginLoading ? 0.7 : 1
              }}
            >
              {loginLoading ? 'Checking...' : 'Check My Status'}
            </button>
            
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button 
                onClick={() => { setShowLogin(false); setShowUpgrade(true); }} 
                style={{ background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontFamily: 'Nunito', fontWeight: 600 }}
              >
                Don't have premium? Upgrade now →
              </button>
            </div>
            
            <button onClick={() => setShowLogin(false)} style={{ marginTop: 10, width: '100%', background: 'none', border: 'none', color: '#8B7355', cursor: 'pointer', fontFamily: 'Nunito' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
